import React, { useEffect, useMemo, useState } from "react";
import {
  Col, Row, Card, CardBody, Badge, Button, Modal, ModalHeader, ModalBody,
  ModalFooter, Spinner, Alert, Input
} from "reactstrap";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { API_BASE, getAuthHeaders, apiJson } from "../../../helpers/apiConfig";
import { downloadObjectsCsv } from "../../../helpers/csvExport";
import { paraCekmeFallback, isMissingEndpoint } from "../../../helpers/financeFallback";

const ParaCekmeTalepleri = () => {
  document.title = "Para Çekme Talepleri | HarmanaGEL";

  const [talepler, setTalepler] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  const [confirmModal, setConfirmModal] = useState(false);
  const [selectedTalep, setSelectedTalep] = useState(null);
  const [action, setAction] = useState(""); // "onay" | "red"
  const [aciklama, setAciklama] = useState("");
  const [bakiyeIade, setBakiyeIade] = useState(true);
  // AdminFinans uçları deploy edilmemişse OData yedek yolu kullanılır
  const [fallbackMode, setFallbackMode] = useState(false);
  const [warnMsg, setWarnMsg] = useState("");
  // Talepteki IBAN, kullanıcının kayıtlı banka hesaplarıyla karşılaştırılır
  const [bankaBilgileri, setBankaBilgileri] = useState(null);
  const [bankaLoading, setBankaLoading] = useState(false);

  const fetchTalepler = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_BASE}/PARA_CEKME_TALEPLERI?$top=2000&$orderby=TALEP_TARIHI desc&$expand=KULLANICILAR($select=AD_SOYAD,Mail_Adress,CEP_NO)`,
        { headers: getAuthHeaders() }
      );
      if (!response.ok) throw new Error(`Yükleme hatası (${response.status})`);
      const result = await response.json();
      if (result && result.value) setTalepler(result.value);
    } catch (err) {
      setError(err.message || "Talepler yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTalepler();
  }, []);

  /** IBAN'ı boşluk/harf farkından bağımsız karşılaştırmak için sadeleştirir */
  const ibanSade = (v) => (v || "").replace(/\s+/g, "").toUpperCase();

  /** Modal açılırken kullanıcının kayıtlı banka hesaplarını çeker */
  const fetchBankaBilgileri = async (kullaniciId) => {
    setBankaBilgileri(null);
    if (!kullaniciId) return;
    setBankaLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/KULLANICI_BANKA_BILGILERI?$filter=KULLANICI_ID eq ${kullaniciId}&$top=10`,
        { headers: getAuthHeaders() }
      );
      if (!res.ok) return;
      const result = await res.json();
      setBankaBilgileri(result?.value || []);
    } catch {
      /* doğrulama opsiyonel — hata onay akışını engellemesin */
    } finally {
      setBankaLoading(false);
    }
  };

  const ibanEslesme = useMemo(() => {
    if (!selectedTalep || !Array.isArray(bankaBilgileri)) return null;
    if (bankaBilgileri.length === 0) return { durum: "kayitsiz" };
    const hedef = ibanSade(selectedTalep.IBAN);
    const eslesen = bankaBilgileri.find((b) => ibanSade(b.IBAN) === hedef);
    return eslesen ? { durum: "eslesti", banka: eslesen } : { durum: "eslesmedi" };
  }, [selectedTalep, bankaBilgileri]);

  const handleAction = async () => {
    if (!selectedTalep) return;
    setActionLoading(true);
    setError(null);
    setWarnMsg("");
    try {
      const path =
        action === "onay"
          ? "/api/AdminFinans/ParaCekmeOnayla"
          : "/api/AdminFinans/ParaCekmeReddet";

      let data;
      try {
        data = await apiJson(path, {
          method: "POST",
          body: JSON.stringify({
            TalepId: selectedTalep.ID,
            Aciklama: aciklama || null,
            BakiyeIade: action === "red" ? bakiyeIade : undefined,
          }),
        });
        setFallbackMode(false);
      } catch (apiErr) {
        // Uç deploy edilmemişse OData yedek yolu; başka hata ise yukarı fırlat
        if (!isMissingEndpoint(apiErr)) throw apiErr;
        setFallbackMode(true);
        data = await paraCekmeFallback(selectedTalep, action, aciklama, bakiyeIade);
      }

      setSuccessMsg(
        data?.message ||
          (action === "onay" ? "Talep onaylandı." : "Talep reddedildi.")
      );
      if (data?.walletError) setWarnMsg(data.walletError);
      setConfirmModal(false);
      setAciklama("");
      await fetchTalepler();
      setTimeout(() => setSuccessMsg(""), 4000);
    } catch (err) {
      setError(err.message || "İşlem sırasında hata oluştu.");
    } finally {
      setActionLoading(false);
    }
  };

  const getDurumBadge = (durum) => {
    const renkler = { Beklemede: "warning", "Onaylandı": "success", Reddedildi: "danger" };
    return <Badge color={renkler[durum] || "secondary"}>{durum || "Bilinmiyor"}</Badge>;
  };

  const bekleyenCount = useMemo(
    () => talepler.filter((t) => t.DURUM === "Beklemede").length,
    [talepler]
  );
  const toplamTutar = useMemo(
    () =>
      talepler
        .filter((t) => t.DURUM === "Beklemede")
        .reduce((s, t) => s + parseFloat(t.MIKTAR || 0), 0),
    [talepler]
  );

  const exportCsv = () => {
    downloadObjectsCsv(
      `para-cekme-${new Date().toISOString().slice(0, 10)}.csv`,
      talepler,
      [
        { key: "ID", label: "ID" },
        { label: "Ad Soyad", get: (t) => t.AD_SOYAD || t.KULLANICILAR?.AD_SOYAD || "" },
        { label: "E-posta", get: (t) => t.KULLANICILAR?.Mail_Adress || "" },
        { label: "Telefon", get: (t) => t.KULLANICILAR?.CEP_NO || "" },
        { key: "MIKTAR", label: "Miktar" },
        { key: "IBAN", label: "IBAN" },
        { key: "DURUM", label: "Durum" },
        {
          label: "Talep Tarihi",
          get: (t) =>
            t.TALEP_TARIHI ? new Date(t.TALEP_TARIHI).toLocaleString("tr-TR") : "",
        },
        {
          label: "İşlem Tarihi",
          get: (t) =>
            t.ISLEM_TARIHI ? new Date(t.ISLEM_TARIHI).toLocaleString("tr-TR") : "",
        },
      ]
    );
  };

  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "ID",
        enableSorting: true,
        enableColumnFilter: false,
        cell: (cell) => <span className="fw-bold">#{cell.getValue()}</span>,
      },
      {
        header: "Kullanıcı",
        accessorKey: "KULLANICILAR",
        enableSorting: false,
        enableColumnFilter: false,
        cell: (cell) => {
          const u = cell.getValue();
          return u ? (
            <div>
              <div className="fw-medium">{u.AD_SOYAD}</div>
              <small className="text-muted">{u.Mail_Adress}</small>
            </div>
          ) : (
            <div className="fw-medium">{cell.row.original.AD_SOYAD}</div>
          );
        },
      },
      {
        header: "Miktar",
        accessorKey: "MIKTAR",
        enableSorting: true,
        enableColumnFilter: false,
        cell: (cell) => (
          <span className="fw-bold text-success fs-5">
            {parseFloat(cell.getValue() || 0).toLocaleString("tr-TR", {
              minimumFractionDigits: 2,
            })}{" "}
            ₺
          </span>
        ),
      },
      {
        header: "IBAN",
        accessorKey: "IBAN",
        enableSorting: false,
        enableColumnFilter: false,
        cell: (cell) => <span className="font-monospace small">{cell.getValue()}</span>,
      },
      {
        header: "Durum",
        accessorKey: "DURUM",
        enableSorting: true,
        enableColumnFilter: false,
        cell: (cell) => getDurumBadge(cell.getValue()),
      },
      {
        header: "Talep Tarihi",
        accessorKey: "TALEP_TARIHI",
        enableSorting: true,
        enableColumnFilter: false,
        cell: (cell) => {
          const d = cell.getValue();
          return d ? new Date(d).toLocaleString("tr-TR") : "-";
        },
      },
      {
        header: "İşlem Tarihi",
        accessorKey: "ISLEM_TARIHI",
        enableSorting: true,
        enableColumnFilter: false,
        cell: (cell) => {
          const d = cell.getValue();
          return d ? new Date(d).toLocaleString("tr-TR") : "-";
        },
      },
      {
        header: "İşlemler",
        enableSorting: false,
        enableColumnFilter: false,
        cell: (cellProps) => {
          const t = cellProps.row.original;
          if (t.DURUM !== "Beklemede") return null;
          return (
            <div className="d-flex gap-2">
              <Button
                color="success"
                size="sm"
                onClick={() => {
                  setSelectedTalep(t);
                  setAction("onay");
                  setAciklama("");

                  setBakiyeIade(true);


                  fetchBankaBilgileri(t.KULLANICI_ID);
                  setConfirmModal(true);
                }}
              >
                <i className="bx bx-check me-1" />
                Onayla
              </Button>
              <Button
                color="danger"
                size="sm"
                outline
                onClick={() => {
                  setSelectedTalep(t);
                  setAction("red");
                  setAciklama("");

                  setBakiyeIade(true);


                  fetchBankaBilgileri(t.KULLANICI_ID);
                  setConfirmModal(true);
                }}
              >
                <i className="bx bx-x me-1" />
                Reddet
              </Button>
            </div>
          );
        },
      },
    ],
    []
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="HarmanaGEL" breadcrumbItem="Para Çekme Talepleri" />

          {successMsg && <Alert color="success">{successMsg}</Alert>}
          {error && (
            <Alert color="danger" toggle={() => setError(null)}>
              {error}
            </Alert>
          )}
          {warnMsg && (
            <Alert color="warning" toggle={() => setWarnMsg("")}>
              <i className="bx bx-error me-1" />
              {warnMsg}
            </Alert>
          )}
          {fallbackMode && (
            <Alert color="info" className="small py-2">
              <i className="bx bx-info-circle me-1" />
              <strong>AdminFinans servisi yayında değil.</strong> İşlemler OData yedek
              yolundan yapılıyor: talep güncellemesi ve cüzdan hareketi ayrı adımlar
              hâlinde çalışır (tek transaction değil). Sonucu cüzdan sayfasından
              doğrulayın.
            </Alert>
          )}

          {bekleyenCount > 0 && (
            <Alert color="warning" className="d-flex align-items-center gap-3">
              <i className="bx bx-time fs-3" />
              <div>
                <strong>{bekleyenCount} bekleyen talep</strong> bulunmaktadır.
                Toplam bekleyen tutar:{" "}
                <strong>
                  {toplamTutar.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺
                </strong>
                <div className="small mt-1 text-muted">
                  Red işleminde bakiye iadesi, modaldaki onay kutusuna göre yapılır.
                </div>
              </div>
            </Alert>
          )}

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                    <h4 className="card-title mb-0">Para Çekme Talepleri</h4>
                    <div className="d-flex gap-2">
                      <Button
                        color="secondary"
                        size="sm"
                        outline
                        onClick={exportCsv}
                        disabled={!talepler.length}
                      >
                        <i className="bx bx-export me-1" />
                        CSV
                      </Button>
                      <Button
                        color="primary"
                        size="sm"
                        onClick={fetchTalepler}
                        disabled={isLoading}
                      >
                        <i className="bx bx-refresh me-1" />
                        Yenile
                      </Button>
                    </div>
                  </div>

                  {isLoading ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" />
                    </div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={talepler}
                      isGlobalFilter={true}
                      SearchPlaceholder="Talep ara..."
                      isPagination={true}
                      isCustomPageSize={true}
                      tableClass="table-bordered align-middle nowrap w-100 mb-0"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      <Modal isOpen={confirmModal} toggle={() => !actionLoading && setConfirmModal(false)} centered>
        <ModalHeader toggle={() => !actionLoading && setConfirmModal(false)}>
          {action === "onay" ? "Talebi Onayla" : "Talebi Reddet"}
        </ModalHeader>
        <ModalBody>
          {selectedTalep && (
            <div>
              <p>
                <strong>
                  {selectedTalep.AD_SOYAD || selectedTalep.KULLANICILAR?.AD_SOYAD}
                </strong>{" "}
                —{" "}
                <strong>
                  {parseFloat(selectedTalep.MIKTAR || 0).toLocaleString("tr-TR", {
                    minimumFractionDigits: 2,
                  })}{" "}
                  ₺
                </strong>
              </p>
              {action === "onay" && (
                <div className="alert alert-info small mb-3">
                  IBAN: <span className="font-monospace">{selectedTalep.IBAN}</span>
                  <br />
                  Onay sonrası tutarı bankadan manuel havale etmeniz gerekir.

                  {/* Talepteki IBAN kullanıcının kayıtlı hesabıyla uyuşuyor mu?
                      Havale öncesi tek doğrulama noktası burası. */}
                  <div className="mt-2 pt-2 border-top">
                    {bankaLoading && (
                      <span className="text-muted">
                        <Spinner size="sm" className="me-1" />
                        Kayıtlı banka bilgileri kontrol ediliyor...
                      </span>
                    )}
                    {!bankaLoading && ibanEslesme?.durum === "eslesti" && (
                      <span className="text-success">
                        <i className="bx bx-check-circle me-1" />
                        <strong>IBAN doğrulandı</strong> — kullanıcının kayıtlı hesabıyla
                        eşleşiyor
                        {ibanEslesme.banka?.BANKA_ADI ? ` (${ibanEslesme.banka.BANKA_ADI})` : ""}
                        {ibanEslesme.banka?.HESAP_SAHIBI
                          ? ` · Hesap sahibi: ${ibanEslesme.banka.HESAP_SAHIBI}`
                          : ""}
                      </span>
                    )}
                    {!bankaLoading && ibanEslesme?.durum === "eslesmedi" && (
                      <span className="text-danger">
                        <i className="bx bx-error-circle me-1" />
                        <strong>DİKKAT: IBAN eşleşmiyor.</strong> Kullanıcının kayıtlı
                        hesapları:{" "}
                        {bankaBilgileri
                          .map((b) => `${b.IBAN}${b.BANKA_ADI ? ` (${b.BANKA_ADI})` : ""}`)
                          .join(", ")}
                        . Havale öncesi kullanıcıyla teyit edin.
                      </span>
                    )}
                    {!bankaLoading && ibanEslesme?.durum === "kayitsiz" && (
                      <span className="text-warning">
                        <i className="bx bx-info-circle me-1" />
                        Kullanıcının kayıtlı banka hesabı yok — IBAN doğrulanamıyor.
                      </span>
                    )}
                  </div>
                </div>
              )}
              {action === "red" && (
                <div className="alert alert-warning small mb-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="bakiyeIadeCheck"
                      checked={bakiyeIade}
                      onChange={(e) => setBakiyeIade(e.target.checked)}
                      disabled={actionLoading}
                    />
                    <label className="form-check-label" htmlFor="bakiyeIadeCheck">
                      <strong>
                        {parseFloat(selectedTalep.MIKTAR || 0).toLocaleString("tr-TR", {
                          minimumFractionDigits: 2,
                        })}{" "}
                        ₺
                      </strong>{" "}
                      kullanıcı cüzdanına iade edilsin
                    </label>
                  </div>
                  <div className="text-muted mt-1">
                    Tutar talep anında bakiyeden düşülmüşse işaretli bırakın. Düşülmediyse
                    işareti kaldırın, yoksa kullanıcıya fazla bakiye yazılır.
                  </div>
                </div>
              )}
              <label className="form-label">Not (opsiyonel)</label>
              <Input
                type="textarea"
                rows={2}
                value={aciklama}
                onChange={(e) => setAciklama(e.target.value)}
                placeholder="İşlem notu..."
                disabled={actionLoading}
              />
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            outline
            onClick={() => setConfirmModal(false)}
            disabled={actionLoading}
          >
            İptal
          </Button>
          <Button
            color={action === "onay" ? "success" : "danger"}
            onClick={handleAction}
            disabled={actionLoading}
          >
            {actionLoading ? <Spinner size="sm" /> : action === "onay" ? "Onayla" : "Reddet + İade"}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default ParaCekmeTalepleri;
